import React, { useState } from "react";
import api from "../lib/api";
import { GitCompare, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ASSETS = ["BTC", "ETH", "SOL", "XRP", "BNB", "ADA", "DOGE", "AVAX", "DOT", "HYPE"];

function fmt(n, opts = {}) {
  if (n === null || n === undefined) return "—";
  return "$" + n.toLocaleString(undefined, { maximumFractionDigits: opts.decimals ?? 0 });
}

function pct(n) {
  if (n === null || n === undefined) return "—";
  const isPos = n >= 0;
  return (
    <span className={isPos ? "text-green-400" : "text-red-400"}>
      {isPos ? "+" : ""}{n.toFixed(2)}%
    </span>
  );
}

export default function CoinCompare() {
  const [coin1, setCoin1] = useState("BTC");
  const [coin2, setCoin2] = useState("ETH");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);


  const pickCoin1 = (val) => {
    setCoin1(val);
    if (val === coin2) setCoin2(ASSETS.find((a) => a !== val));
  };
  const pickCoin2 = (val) => {
    setCoin2(val);
    if (val === coin1) setCoin1(ASSETS.find((a) => a !== val));
  };

  const compare = async () => {
    if (coin1 === coin2) return toast.error("Choose two different coins");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/tools/coin-compare", { params: { coin1, coin2 } });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compare these coins");
    } finally { setLoading(false); }
  };

  const ROWS = [
    { label: "Price", key: "price", fmt: (v) => fmt(v, { decimals: 2 }) },
    { label: "Market Cap", key: "market_cap", fmt: fmt },
    { label: "24h Volume", key: "volume_24h", fmt: fmt },
    { label: "Circulating Supply", key: "circulating_supply", fmt: (v) => v ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—" },
    { label: "Max Supply", key: "max_supply", fmt: (v) => v ? v.toLocaleString() : "Unlimited" },
    { label: "All-Time High", key: "ath", fmt: (v) => fmt(v, { decimals: 2 }) },
    { label: "From ATH", key: "ath_change_pct", fmt: pct },
    { label: "24h Change", key: "change_24h", fmt: pct },
    { label: "7d Change", key: "change_7d", fmt: pct },
    { label: "30d Change", key: "change_30d", fmt: pct },
    { label: "1y Change", key: "change_1y", fmt: pct },
    { label: "GitHub Stars", key: "github_stars", fmt: (v) => v?.toLocaleString() ?? "—" },
    { label: "Commits (4wk)", key: "github_commits_4wk", fmt: (v) => v ?? "—" },
  ];

  return (
    <div className="pt-28 pb-20 max-w-3xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-10">
        <GitCompare className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
        <h1 className="font-display font-bold text-4xl text-[var(--text-primary)]">Coin Compare</h1>
        <p className="mt-3 text-[var(--text-secondary)]">
          Compare market cap, supply, performance, and developer activity side by side.
        </p>
      </div>

      <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6 flex flex-col sm:flex-row items-center gap-4">
        <select value={coin1} onChange={(e) => pickCoin1(e.target.value)}
          className="w-full sm:w-auto flex-1 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
          {ASSETS.filter((a) => a !== coin2).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <span className="text-[var(--text-tertiary)] font-mono text-sm">vs</span>
        <select value={coin2} onChange={(e) => pickCoin2(e.target.value)}
          className="w-full sm:w-auto flex-1 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
          {ASSETS.filter((a) => a !== coin1).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <button onClick={compare} disabled={loading}
          className="w-full sm:w-auto bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-3 hover:bg-yellow-500 transition disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4" />}
          Compare
        </button>
      </div>

      {result && (
        <div className="mt-6 bg-[var(--bg-panel)] border border-[var(--border-soft)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-soft)]">
                <th className="text-left p-4 text-xs font-mono text-[var(--text-tertiary)] uppercase">Metric</th>
                <th className="text-right p-4 text-yellow-400 font-bold">{result.coin1.symbol}</th>
                <th className="text-right p-4 text-yellow-400 font-bold">{result.coin2.symbol}</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.key} className="border-b border-[var(--border-soft)] last:border-0">
                  <td className="p-4 text-[var(--text-tertiary)] font-mono text-xs">{row.label}</td>
                  <td className="p-4 text-right text-[var(--text-primary)]">{row.fmt(result.coin1[row.key])}</td>
                  <td className="p-4 text-right text-[var(--text-primary)]">{row.fmt(result.coin2[row.key])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-8 text-xs text-[var(--text-tertiary)] text-center">
        Data from CoinGecko. Developer activity reflects public GitHub repositories only. Not financial advice.
      </p>

      <div className="mt-16 border-t border-[var(--border-soft)] pt-10">
        <h2 className="font-display font-bold text-2xl text-[var(--text-primary)] mb-6">How this comparison works</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">1. Pick two coins</div>
            <p className="text-xs text-[var(--text-secondary)]">Choose any two supported coins to compare directly against each other.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">2. Pull live data</div>
            <p className="text-xs text-[var(--text-secondary)]">Market cap, supply, all-time high, price performance, and GitHub development activity are fetched fresh for both coins.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">3. Read side by side</div>
            <p className="text-xs text-[var(--text-secondary)]">Every metric lines up in a single table, making it easy to see where one coin leads or lags the other.</p>
          </div>
        </div>

        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
          Market cap and volume show relative size and trading activity, while circulating and max supply reveal how scarce or inflationary a coin's issuance model is. Performance figures across multiple timeframes help separate short-term noise from longer-term trend.
        </p>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
          GitHub stars and recent commit activity offer a rough proxy for how actively a project is being developed, though they don't capture private development, non-GitHub repositories, or the quality of the work being done. This tool is for informational comparison only and is not financial advice.
        </p>
      </div>
    </div>
  );
}